import {makeForgotPasswordResendToken, makeSignUpResendToken} from 'main/factories/usecases';
import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {translations} from 'presentation/translations-new';
import {useEffect, useState} from 'react';
import {PublicFlowEnum} from '../../constants/PublicFlowEnum';
import {useSignUpFormDataStore} from '../../sign-up/store/useSignUpFormDataStore';
import {useForgotPasswordData} from '../useForgotPasswordData';
import {UseResendTokenProps} from './types';

const {resendToken} = translations['pt-br'].publicFlow;

export function useResendToken({methods, flowType}: UseResendTokenProps) {
	const isSignup = flowType === PublicFlowEnum.SIGN_UP;
	const {
		formState: {isValidating},
	} = methods;

	const [requestState, setRequestState] = useState<OperationStateType | null>(null);
	const [apiErrorMsg, setApiErrorMsg] = useState('');
	const [resendModalOpenState, setResendModalOpenState] = useState(false);
	const signUpFormData = useSignUpFormDataStore(state => state.signUpFormData);
	const showAPIError = requestState === OperationStateTypesEnum.ERROR;
	const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
	const {data: forgotPasswordFormData} = useForgotPasswordData();

	const {cdEmpresaPlano, dtNascimento, id, tipoId} = forgotPasswordFormData;

	const {
		dataHostNameState: {productCode},
	} = useHostName();

	const handleOpenSnackbar = () => {
		setOpenSnackbar(true);
	};

	const handleCloseSnackbar = () => {
		setOpenSnackbar(false);
	};

	const toggleResendModal = () => {
		setResendModalOpenState(!resendModalOpenState);
	};

	const handleResendToken = async (privateFlow?: boolean) => {
		const privateParams = {
			cdPessoa: String(forgotPasswordFormData.cdPessoa) ?? '',
			cdEmpresaPlano: productCode,
		};

		const publicParamsSignup = {
			cdPessoa: signUpFormData.cdPessoa,
			cdEmpresaPlano: signUpFormData.cdEmpresaPlano,
		};

		const isErrorCdPessoaPrivate =
			!privateParams.cdPessoa ||
			privateParams.cdPessoa === 'undefined' ||
			privateParams.cdPessoa === '0' ||
			privateParams.cdPessoa?.length === 0;

		const isErrorCdPessoaPublic =
			!publicParamsSignup.cdPessoa ||
			publicParamsSignup.cdPessoa === 'undefined' ||
			publicParamsSignup.cdPessoa === '0' ||
			publicParamsSignup.cdPessoa?.length === 0;

		const isErrorCdPessoa =
			(privateFlow && isErrorCdPessoaPrivate) || (isSignup && isErrorCdPessoaPublic);
		try {
			setRequestState(OperationStateTypesEnum.PENDING);
			if (isErrorCdPessoa) {
				throw new Error('cdPessoa is required');
			}
			if (isSignup) {
				await makeSignUpResendToken().resendToken(publicParamsSignup);
			} else if (privateFlow) {
				await makeSignUpResendToken().resendToken(privateParams);
			} else {
				await makeForgotPasswordResendToken().resendToken({
					cdEmpresaPlano,
					dtNascimento,
					id,
					tipoId,
				});
			}
			setRequestState(OperationStateTypesEnum.DONE);
		} catch (error) {
			setRequestState(OperationStateTypesEnum.ERROR);
			setApiErrorMsg(resendToken.errorMessage);
		} finally {
			handleOpenSnackbar();
			if (!privateFlow) {
				toggleResendModal();
			}
		}
	};

	const clearAPIError = () => {
		setRequestState(null);
	};

	useEffect(() => {
		if (isValidating) {
			clearAPIError();
		}
	}, [isValidating]);

	return {
		clearAPIError,
		requestState,
		apiErrorMsg,
		resendModalOpenState,
		signUpFormData,
		showAPIError,
		openSnackbar,
		handleOpenSnackbar,
		handleCloseSnackbar,
		handleResendToken,
		toggleResendModal,
	};
}
